import React, { createContext, useContext, useState, ReactNode } from 'react';
import { login as apiLogin } from '../services/api';

interface AuthContextType {
  isLoggedIn: boolean;
  login: (password: string) => Promise<boolean>;
  logout: () => void;
  error: string | null;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode | ((context: Pick<AuthContextType, 'isLoggedIn'>) => ReactNode);
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [isLoggedIn, setIsLoggedIn] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const login = async (password: string): Promise<boolean> => {
    try {
      const response = await apiLogin({ password });
      if (response.success) {
        setIsLoggedIn(true);
        setError(null);
        return true;
      } else {
        setError(response.message || '로그인에 실패했습니다.');
        return false;
      }
    } catch (err) {
      setError('서버 연결에 실패했습니다.');
      return false;
    }
  };

  const logout = () => {
    setIsLoggedIn(false);
  };

  const renderChildren = () => {
    if (typeof children === 'function') {
      return children({ isLoggedIn });
    }
    return children;
  };

  return (
    <AuthContext.Provider value={{ isLoggedIn, login, logout, error }}>
      {renderChildren()}
    </AuthContext.Provider>
  );
};
